/**
 * The application on a phone.
 *
 * The desktop shell answers "where am I?" with two rows of navigation, a
 * labelled organization control, a labelled workspace control, and the
 * mission. Stacked on a phone that is most of the first screen, and the work
 * begins below the fold. So the phone gets its own shell for the same
 * application: the same routes, the same permissions, the same navigation
 * definition in `lib/navigation.ts`, drawn for a narrow screen.
 *
 * Above the page there is one compact bar of about 56 pixels — the mark, the
 * organization and workspace as one line of context, and the account — and
 * nothing else. The navigation moves to the bottom, where a thumb reaches it,
 * and the sections of the current area become tabs inside the page. What a
 * person came to read starts in the first screen.
 *
 * Nothing here decides access. Every link is filtered by the same
 * `sectionVisible` the desktop uses, from permissions the server sent, and
 * the server refuses the request again whatever the browser drew.
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { AskCapture } from "../ask-capture.tsx";
import { Icon, type IconName } from "../icon.tsx";
import { QuickNoteCapture } from "../quick-note.tsx";
import { ReportProblem } from "../report-problem.tsx";
import { ThemeChoices } from "../theme-menu.tsx";
import { classes } from "../primitives.tsx";
import {
  PHONE_TABS,
  areasInGroup,
  firstRouteOf,
  groupOf,
  visibleSections,
  type NavigationGroup,
  type Visibility,
} from "../../lib/navigation.ts";
import type { SessionView } from "../../lib/api.ts";
import { Sheet, SheetGroup, SheetRow } from "./sheet.tsx";

interface WorkspaceChoice {
  readonly frameworkKey: string;
  readonly shortName: string;
}

export interface MobileShellProps {
  readonly view: SessionView;
  readonly where: Visibility;
  readonly pathname: string;
  readonly workspaces: readonly WorkspaceChoice[];
  readonly onSelectTenant: (tenantId: string) => void;
  readonly onSelectWorkspace: (workspaceKey: string | null) => void;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}

/** The top bar: the mark, the context of the work, and the account. */
export function MobileTopBar({
  view,
  where,
  workspaces,
  onSelectTenant,
  onSelectWorkspace,
  onSignOut,
}: Omit<MobileShellProps, "children" | "pathname">) {
  const [context, setContext] = useState(false);
  const [account, setAccount] = useState(false);
  const selected = view.organizations.find((row) => row.tenantId === view.selectedTenantId);
  const workspace =
    view.selectedWorkspaceKey === null
      ? "Every framework"
      : workspaces.find((row) => row.frameworkKey === view.selectedWorkspaceKey)?.shortName
        ?? "Every framework";

  return (
    <>
      <div className="flex h-14 items-center gap-2 px-3">
        <Link to="/" className="flex shrink-0 items-center" aria-label="ArcGRC, my work">
          <img src="/brand/arcgrc-icon-512.png" alt="" aria-hidden="true" className="h-7 w-7" />
        </Link>
        <button
          type="button"
          data-testid="mobile-context"
          aria-haspopup="dialog"
          aria-expanded={context}
          className="flex min-w-0 flex-1 items-center gap-1 rounded-arc px-2 py-1 text-left hover:bg-selected"
          onClick={() => setContext(true)}
        >
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold leading-tight">
              {selected?.name ?? "Select an organization"}
            </span>
            <span className="block truncate text-xs text-muted">{workspace}</span>
          </span>
          <span aria-hidden="true" className="ml-auto shrink-0 text-muted">
            <Icon name="chevron-down" size="0.7rem" />
          </span>
        </button>
        <button
          type="button"
          data-testid="mobile-account"
          aria-haspopup="dialog"
          aria-expanded={account}
          aria-label={`Account: ${view.user.displayName}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-divider text-sm font-semibold text-muted"
          onClick={() => setAccount(true)}
        >
          {initialsOf(view.user.displayName)}
        </button>
      </div>

      <ContextSheet
        open={context}
        onClose={() => setContext(false)}
        view={view}
        workspaces={workspaces}
        onSelectTenant={onSelectTenant}
        onSelectWorkspace={onSelectWorkspace}
      />
      <AccountSheet
        open={account}
        onClose={() => setAccount(false)}
        view={view}
        where={where}
        onSignOut={onSignOut}
      />
    </>
  );
}

/** Two letters for the account button. A name never fits a phone's top bar. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase();
}

/** The organization and the workspace, chosen in one place. */
function ContextSheet({
  open,
  onClose,
  view,
  workspaces,
  onSelectTenant,
  onSelectWorkspace,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly view: SessionView;
  readonly workspaces: readonly WorkspaceChoice[];
  readonly onSelectTenant: (tenantId: string) => void;
  readonly onSelectWorkspace: (workspaceKey: string | null) => void;
}) {
  const navigate = useNavigate();
  return (
    <Sheet title="Organization and workspace" open={open} onClose={onClose} testId="context-sheet">
      <SheetGroup label="Organization">
        {view.organizations.map((row) => (
          <SheetRow
            key={row.tenantId}
            selected={row.tenantId === view.selectedTenantId}
            onClick={() => {
              if (row.tenantId !== view.selectedTenantId) onSelectTenant(row.tenantId);
              onClose();
            }}
          >
            {row.name}
          </SheetRow>
        ))}
      </SheetGroup>

      {workspaces.length > 0 && (
        <SheetGroup label="Workspace">
          <SheetRow
            selected={view.selectedWorkspaceKey === null}
            onClick={() => {
              onSelectWorkspace(null);
              onClose();
            }}
          >
            Every framework
          </SheetRow>
          {workspaces.map((row) => (
            <SheetRow
              key={row.frameworkKey}
              selected={row.frameworkKey === view.selectedWorkspaceKey}
              onClick={() => {
                onSelectWorkspace(row.frameworkKey);
                onClose();
              }}
            >
              {row.shortName}
            </SheetRow>
          ))}
        </SheetGroup>
      )}

      {/*
        Every signed-in person may make an organization, exactly as on the
        desktop, so this is not hidden behind an answer from the server. A
        hidden control that should be there is worse than one that answers
        with a reason.
      */}
      <div className="border-t border-divider pt-2">
        <SheetRow
          onClick={() => {
            onClose();
            void navigate({ to: "/organizations/new" });
          }}
        >
          Create organization
        </SheetRow>
      </div>
    </Sheet>
  );
}

/** The account, and the few things that belong to one person. */
function AccountSheet({
  open,
  onClose,
  view,
  where,
  onSignOut,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly view: SessionView;
  readonly where: Visibility;
  readonly onSignOut: () => void;
}) {
  const navigate = useNavigate();
  // The organization's settings, for those who manage them. Everyone has their own.
  const settings = where.permissions.includes("organization.manage");
  return (
    <Sheet title={view.user.displayName} open={open} onClose={onClose} testId="account-sheet">
      <p className="px-2 pb-2 text-sm text-muted">{view.user.email}</p>
      <div role="menu" aria-label="Account" className="flex flex-col">
        <SheetRow
          onClick={() => {
            onClose();
            void navigate({ to: "/account" });
          }}
        >
          Your settings
        </SheetRow>
        <SheetRow
          onClick={() => {
            onClose();
            void navigate({ to: "/security/me" });
          }}
        >
          My security
        </SheetRow>
        {settings && (
          <SheetRow
            onClick={() => {
              onClose();
              void navigate({ to: "/settings" });
            }}
          >
            Organization settings
          </SheetRow>
        )}
        {view.accountUrl !== null && (
          <a
            role="menuitem"
            href={view.accountUrl}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-11 items-center rounded-arc px-2 py-2 hover:bg-selected"
            onClick={onClose}
          >
            Manage passkeys
          </a>
        )}
        <ThemeChoices onChosen={onClose} />
        <div className="px-2 py-2">
          <ReportProblem />
        </div>
        <SheetRow onClick={onSignOut}>Sign out</SheetRow>
      </div>
    </Sheet>
  );
}

/**
 * The navigation, along the bottom.
 *
 * Three tabs and a menu. Each tab carries its own word: an icon alone asks a
 * person to remember what a folder meant here, and the answer is different in
 * every application they use.
 */
export function MobileBottomNav({
  where,
  pathname,
  onMore,
  moreOpen,
}: {
  readonly where: Visibility;
  readonly pathname: string;
  readonly onMore: () => void;
  readonly moreOpen: boolean;
}) {
  const current = groupOf(pathname);
  return (
    <nav
      aria-label="Main"
      data-testid="bottom-nav"
      className="arc-bottom-nav fixed inset-x-0 bottom-0 z-30 flex border-t border-divider bg-paper tablet:hidden"
    >
      {PHONE_TABS.map((tab) => {
        const to = firstRouteOf(tab.group, where);
        if (to === null) return null;
        const active = current === tab.group && !moreOpen;
        return (
          <Link
            key={tab.group}
            to={to}
            data-testid={`tab-${tab.group}`}
            aria-current={active ? "page" : undefined}
            className={classes(
              "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs",
              active ? "font-semibold text-accent" : "text-muted",
            )}
          >
            <Icon name={tab.icon} size="1.1rem" />
            {tab.label}
          </Link>
        );
      })}
      <button
        type="button"
        data-testid="tab-more"
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        aria-current={current === "more" || moreOpen ? "page" : undefined}
        className={classes(
          "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs",
          current === "more" || moreOpen ? "font-semibold text-accent" : "text-muted",
        )}
        onClick={onMore}
      >
        <Icon name="ellipsis" size="1.1rem" />
        More
      </button>
    </nav>
  );
}

/** Everything the three tabs do not hold, and the two capture actions. */
export function MoreSheet({
  open,
  onClose,
  where,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly where: Visibility;
}) {
  const navigate = useNavigate();
  const areas = areasInGroup("more", where);
  return (
    <Sheet title="More" open={open} onClose={onClose} testId="more-sheet">
      {/*
        The two things a person writes from anywhere. Their panels open above
        this sheet rather than closing it, because a button that unmounts the
        panel it just opened opens nothing at all.
      */}
      <div className="mb-3 flex gap-2">
        <QuickNoteCapture inline />
        <AskCapture inline />
      </div>
      {areas.map((area) => (
        <SheetGroup key={area.key} label={area.label}>
          {visibleSections(area, where).map((section) => (
            <SheetRow
              key={section.to}
              icon={<Icon name={section.icon} size="1rem" />}
              onClick={() => {
                onClose();
                void navigate({ to: section.to });
              }}
            >
              {section.label}
            </SheetRow>
          ))}
        </SheetGroup>
      ))}
    </Sheet>
  );
}

/**
 * The sections of the current area, as tabs inside the page.
 *
 * Secondary navigation belongs to the page, not to the application's bar. It
 * scrolls sideways when there are more sections than the width holds — the
 * strip scrolls, never the page.
 */
export function MobilePageTabs({
  areas,
  pathname,
}: {
  /** The areas of the phone tab, each with its sections. */
  readonly areas: readonly { readonly label: string; readonly sections: readonly { readonly to: string; readonly label: string; readonly icon: IconName }[] }[];
  readonly pathname: string;
}) {
  const count = areas.reduce((sum, area) => sum + area.sections.length, 0);
  if (count < 2) return null;
  // Every section is in view: the links wrap onto as many rows as they need.
  // A row that scrolls sideways hid most of a tab's sections off the screen.
  // Where a tab holds two areas, each area's links start with its name.
  return (
    <nav
      aria-label="Sections"
      data-testid="page-tabs"
      className="-mx-4 mb-3 flex flex-col gap-1 border-b border-divider px-4 pb-2 tablet:hidden"
    >
      {areas.map((area) => (
        <div key={area.label} className="flex flex-wrap items-center gap-1" role={areas.length > 1 ? "group" : undefined} aria-label={areas.length > 1 ? area.label : undefined}>
          {areas.length > 1 && <span className="w-full text-xs font-medium text-muted" aria-hidden="true">{area.label}</span>}
          {area.sections.map((section) => {
            const active =
              section.to === "/" ? pathname === "/" : pathname.startsWith(section.to);
            return (
              <Link
                key={section.to}
                to={section.to}
                aria-current={active ? "page" : undefined}
                className={classes(
                  "inline-flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-arc px-3 py-1.5 text-sm",
                  active ? "bg-selected font-semibold text-accent" : "text-muted",
                )}
              >
                <Icon name={section.icon} size="0.85rem" />
                {section.label}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/** The phone's shell around one page. */
export function MobileShell({
  view,
  where,
  pathname,
  workspaces,
  onSelectTenant,
  onSelectWorkspace,
  onSignOut,
  children,
}: MobileShellProps) {
  const [more, setMore] = useState(false);
  return (
    <div className="min-h-screen">
      <header className="arc-top-bar sticky top-0 z-20 border-b border-divider bg-paper">
        <MobileTopBar
          view={view}
          where={where}
          workspaces={workspaces}
          onSelectTenant={onSelectTenant}
          onSelectWorkspace={onSelectWorkspace}
          onSignOut={onSignOut}
        />
      </header>
      {children}
      <MobileBottomNav
        where={where}
        pathname={pathname}
        moreOpen={more}
        onMore={() => setMore(true)}
      />
      <MoreSheet open={more} onClose={() => setMore(false)} where={where} />
    </div>
  );
}

export type { NavigationGroup };
